import { createContext, useEffect, useMemo, useState } from "react";
import { alpha, createTheme } from "@mui/material/styles";

const STORAGE_KEY = "absolluty-color-mode";
const bodyFont = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const headingFont = '"Plus Jakarta Sans", "Inter", system-ui, sans-serif';
const monoFont = '"JetBrains Mono", ui-monospace, Consolas, monospace';

export const tokens = (mode) => {
  const isDark = mode === "dark";

  return {
    grey: isDark
      ? {
          100: "#f4f8fc",
          200: "#d9e8f1",
          300: "#a9c0d0",
          400: "#829cae",
          500: "#648095",
          700: "#274151",
          900: "#020d17",
        }
      : {
          100: "#0f172a",
          200: "#1e293b",
          300: "#334155",
          400: "#64748b",
          500: "#94a3b8",
          700: "#cbd5e1",
          900: "#f8fafc",
        },
    primary: isDark
      ? {
          900: "#020d17",
          800: "#071b2a",
          700: "#102b3b",
          500: "#0b2434",
          400: "#14384a",
        }
      : {
          900: "#e2e8f0",
          800: "#ffffff",
          700: "#edf2f5",
          500: "#ffffff",
          400: "#f8fafc",
        },
    greenAccent: { 400: isDark ? "#47cff4" : "#007b99", 500: isDark ? "#00d9f5" : "#007b99" },
    blueAccent: { 400: "#73aaf0", 500: "#2870e0" },
    redAccent: { 400: "#f77783", 500: "#ce485b" },
    violetAccent: { 400: "#a78bfa", 500: "#7c3aed" },
    status: {
      alert: isDark ? "#f77783" : "#b92d3d",
      risk: isDark ? "#f3b85b" : "#966000",
      normal: isDark ? "#40d6a7" : "#08764e",
      info: isDark ? "#47cff4" : "#007b99",
    },
    glass: isDark
      ? {
          card: "linear-gradient(155deg, #0d2939 0%, #081b2a 100%)",
          strong: "linear-gradient(145deg, #0b2636 0%, #061a29 100%)",
          border: "rgba(71,207,244,0.22)",
          hoverBorder: "rgba(0,217,245,0.55)",
          shadow: "0 10px 28px rgba(0,5,12,0.22)",
        }
      : {
          card: "#ffffff",
          strong: "rgba(255, 255, 255, 0.9)",
          border: "rgba(15, 23, 42, 0.10)",
          hoverBorder: "rgba(37, 99, 235, 0.32)",
          shadow: "none",
        },
  };
};

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";

  const main = isDark ? "#00d9f5" : "#007b99";
  const border = colors.glass.border;
  const surface = colors.primary[800];
  const raised = colors.primary[700];
  const foreground = colors.grey[100];
  const muted = colors.grey[300];
  const focus = { outline: `2px solid ${main}`, outlineOffset: 3 };
  const overlayShadow = isDark ? "0 16px 36px rgba(0,5,12,0.38)" : "0 12px 32px rgba(23,33,43,0.12)";
  const chartColors = isDark ? ["#00d9f5", "#40d6a7", "#f3b85b", "#73aaf0", "#f77783", "#aa9cf0"] : ["#007b99", "#08764e", "#966000", "#2870e0", "#b92d3d", "#7462b8"];
  const heading = (fontSize, fontWeight = 600) => ({ fontFamily: headingFont, fontSize, fontWeight, lineHeight: 1.4, letterSpacing: 0 });

  return {
    palette: {
      mode,
      primary: { main, contrastText: isDark ? "#001f28" : "#ffffff" },
      secondary: { main: isDark ? "#73aaf0" : "#4864b0" },
      success: { main: colors.status.normal }, warning: { main: colors.status.risk },
      error: { main: colors.status.alert }, info: { main: colors.status.info },
      background: {
        default: isDark ? "#020d17" : "#f4f7f9",
        paper: isDark ? "#071b2a" : "#ffffff",
      },
      text: {
        primary: isDark ? "#f4f8fc" : "#0f172a",
        secondary: isDark ? "#a9c0d0" : "#475569",
      },
      status: colors.status,
      divider: border,
      action: { hover: alpha(main, 0.08), selected: alpha(main, 0.16), focus: alpha(main, 0.2), disabledOpacity: 0.45 },
    },
    typography: {
      fontFamily: bodyFont,
      h1: heading(24, 700), h2: heading(24, 700), h3: heading(24, 700),
      h4: heading(22, 700), h5: heading(20, 700), h6: heading(18, 600),
      body1: { fontSize: 16, lineHeight: 1.6, letterSpacing: 0 },
      body2: { fontSize: 14, lineHeight: 1.5, letterSpacing: 0 },
      caption: { fontSize: 12, lineHeight: 1.5, letterSpacing: 0 },
      overline: { fontSize: 12, fontWeight: 600, letterSpacing: 0 },
      button: { fontSize: 14, fontWeight: 600, textTransform: "none", letterSpacing: 0 },
    },
    shape: { borderRadius: 8 },
    custom: {
      borderRadius: "8px",
      navigationRadius: "8px",
      monoFont, headingFont, overlayShadow,
      softGlow: isDark ? "0 0 20px rgba(0,217,245,0.14)" : "0 0 20px rgba(0,123,153,0.10)",
      metric: { fontFamily: monoFont, fontSize: 32, fontWeight: 700, lineHeight: 1.35, fontVariantNumeric: "tabular-nums", letterSpacing: 0, overflowWrap: "anywhere" },
      surface: { base: surface, raised, inset: isDark ? "#041522" : "#f4f7f9", overlay: colors.glass.strong },
      chart: {
        colors: chartColors, grid: border, axis: colors.grey[400],
        tooltip: { backgroundColor: surface, border: `1px solid ${border}`, borderRadius: 8, color: foreground, fontFamily: monoFont, fontSize: 12, boxShadow: overlayShadow },
        nivo: {
          fontFamily: bodyFont, fontSize: 12, textColor: muted,
          axis: { domain: { line: { stroke: border } }, ticks: { line: { stroke: border }, text: { fill: muted } }, legend: { text: { fill: muted } } },
          grid: { line: { stroke: border, strokeDasharray: "3 5" } },
          legends: { text: { fill: muted } }, labels: { text: { fill: foreground } },
          tooltip: { container: { background: surface, color: foreground, fontFamily: monoFont, fontSize: 12, borderRadius: 8, border: `1px solid ${border}`, boxShadow: overlayShadow } },
        },
      },
      glass: {
        background: colors.glass.card,
        backdropFilter: "none",
        border: `1px solid ${colors.glass.border}`,
        boxShadow: colors.glass.shadow,
      },
      pageGradient: isDark ? "linear-gradient(180deg, #020d17 0%, #061724 100%)" : "#f4f7f9",
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: isDark ? "#020d17" : "#f4f7f9",
          },
          ".dashboard-shell, .dashboard-modal": {
            "--dashboard-bg": isDark ? "#020d17" : "#f4f7f9", "--dashboard-surface": surface,
            "--dashboard-raised": raised, "--dashboard-text": foreground, "--dashboard-muted": muted,
            "--dashboard-border": border, "--dashboard-primary": main, "--dashboard-focus": alpha(main, 0.14),
            "--dashboard-inset": isDark ? "#041522" : "#f4f7f9",
            "--dashboard-glow": isDark ? "0 0 18px rgba(0,217,245,0.12)" : "none",
            "--dashboard-page": isDark ? "linear-gradient(180deg, #020d17 0%, #061724 100%)" : "#f4f7f9",
          },
          ".dashboard-shell :focus-visible": focus,
          "@media (prefers-reduced-motion: reduce)": {
            ".dashboard-shell *, .MuiDialog-root *, .MuiPopover-root *": { animationDuration: "0.01ms !important", animationIterationCount: "1 !important", transitionDuration: "0.01ms !important", scrollBehavior: "auto !important" },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: isDark ? "linear-gradient(155deg, #0d2939 0%, #081b2a 100%)" : "none",
            borderColor: border,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: "none",
            minHeight: 36, padding: "7px 14px",
            transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
            "&.Mui-focusVisible": focus,
          },
          containedPrimary: { backgroundColor: main, color: isDark ? "#00232d" : "#ffffff", "&:hover": { backgroundColor: isDark ? "#47cff4" : "#00627a", boxShadow: isDark ? "0 0 16px rgba(0,217,245,0.2)" : "none" } },
          outlined: { borderColor: border, "&:hover": { borderColor: main, backgroundColor: alpha(main, 0.06) } },
          sizeSmall: { minHeight: 32, fontSize: 12, padding: "5px 10px" },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#041522" : "#ffffff",
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? colors.glass.hoverBorder : muted },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: 1, borderColor: main },
            "&.Mui-focused": { boxShadow: isDark ? "0 0 0 2px rgba(0,217,245,0.12)" : "none" },
          },
          notchedOutline: { borderColor: border },
          input: { padding: "10px 12px" },
        },
      },
      MuiCard: { defaultProps: { elevation: 0 }, styleOverrides: { root: { border: `1px solid ${border}`, borderRadius: 8, boxShadow: colors.glass.shadow, background: colors.glass.card } } },
      MuiCardContent: { styleOverrides: { root: { padding: 24, "&:last-child": { paddingBottom: 24 } } } },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 8, color: muted, "&.Mui-focusVisible": focus, "&:hover": { color: main, backgroundColor: alpha(main, 0.08) } } } },
      MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },
      MuiFormControl: { defaultProps: { size: "small" } },
      MuiInputLabel: { styleOverrides: { root: { color: muted, fontSize: 14 } } },
      MuiFormHelperText: { styleOverrides: { root: { fontSize: 12, marginLeft: 0 } } },
      MuiSelect: { styleOverrides: { icon: { color: muted } } },
      MuiChip: { defaultProps: { size: "small" }, styleOverrides: { root: { borderRadius: 999, fontSize: 12, fontWeight: 500, maxWidth: "100%" }, label: { paddingLeft: 10, paddingRight: 10 } } },
      MuiTabs: { styleOverrides: { root: { minHeight: 42 }, indicator: { height: 2, borderRadius: 2, backgroundColor: main } } },
      MuiTab: { styleOverrides: { root: { minHeight: 42, minWidth: 0, padding: "10px 14px", fontSize: 14, fontWeight: 500, textTransform: "none", color: muted, "&.Mui-selected": { color: main, backgroundColor: alpha(main, 0.12) }, "& .MuiSvgIcon-root": { fontSize: 18 } } } },
      MuiToggleButton: { styleOverrides: { root: { borderColor: border, padding: "7px 12px", color: muted, textTransform: "none", "&.Mui-selected": { color: isDark ? "#00232d" : main, backgroundColor: isDark ? main : alpha(main, 0.12) }, "&.Mui-selected:hover": { backgroundColor: isDark ? "#47cff4" : alpha(main, 0.18) } } } },
      MuiTableCell: { styleOverrides: { root: { padding: "12px 16px", borderColor: border, fontSize: 14 }, head: { backgroundColor: isDark ? "#061a29" : raised, color: muted, fontSize: 12, fontWeight: 600 } } },
      MuiTableRow: { styleOverrides: { root: { "&.MuiTableRow-hover:hover": { backgroundColor: alpha(main, 0.06) } } } },
      MuiDataGrid: { styleOverrides: { root: { border: `1px solid ${border}`, borderRadius: 8, color: foreground, fontSize: 14, backgroundColor: surface, "& .MuiDataGrid-columnHeaders": { backgroundColor: isDark ? "#061a29" : raised, borderColor: border, fontSize: 12 }, "& .MuiDataGrid-cell, & .MuiDataGrid-footerContainer": { borderColor: border }, "& .MuiDataGrid-row:hover": { backgroundColor: alpha(main, 0.06) }, "& .MuiDataGrid-row.Mui-selected": { backgroundColor: alpha(main, 0.12) } } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 8, border: `1px solid ${border}`, boxShadow: overlayShadow, margin: 16, maxHeight: "calc(100% - 32px)" } } },
      MuiDialogTitle: { styleOverrides: { root: { fontFamily: headingFont, fontSize: 18, fontWeight: 600, padding: "16px 20px", borderBottom: `1px solid ${border}` } } },
      MuiDialogContent: { styleOverrides: { root: { padding: 20, "&.MuiDialogContent-dividers": { borderColor: border } } } },
      MuiDialogActions: { styleOverrides: { root: { padding: "12px 20px", gap: 8, borderTop: `1px solid ${border}` } } },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: surface, borderColor: border, backgroundImage: isDark ? "linear-gradient(180deg, #071b2a, #041522)" : "none" } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 8, border: `1px solid ${border}`, boxShadow: overlayShadow, backgroundColor: surface } } },
      MuiPopover: { styleOverrides: { paper: { borderRadius: 8, border: `1px solid ${border}`, boxShadow: overlayShadow, backgroundColor: surface } } },
      MuiMenuItem: { styleOverrides: { root: { minHeight: 36, fontSize: 14, "&:hover": { backgroundColor: alpha(main, 0.08) }, "&.Mui-selected": { color: main, backgroundColor: alpha(main, 0.14) } } } },
      MuiTooltip: { defaultProps: { arrow: true }, styleOverrides: { tooltip: { backgroundColor: raised, color: foreground, border: `1px solid ${border}`, borderRadius: 8, fontSize: 12, padding: "8px 12px", boxShadow: overlayShadow }, arrow: { color: raised } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 8, padding: "8px 12px", border: `1px solid ${border}`, alignItems: "center", fontSize: 14 }, message: { minWidth: 0, overflowWrap: "anywhere" }, standardError: { backgroundColor: alpha(colors.status.alert, 0.08), color: foreground }, standardWarning: { backgroundColor: alpha(colors.status.risk, 0.08), color: foreground }, standardSuccess: { backgroundColor: alpha(colors.status.normal, 0.08), color: foreground }, standardInfo: { backgroundColor: alpha(main, 0.08), color: foreground } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: alpha(muted, 0.09) }, rounded: { borderRadius: 8 } } },
      MuiLinearProgress: { styleOverrides: { root: { height: 4, borderRadius: 4, backgroundColor: alpha(muted, 0.12) } } },
      MuiAccordion: { defaultProps: { elevation: 0 }, styleOverrides: { root: { border: `1px solid ${border}`, backgroundColor: surface, "&:before": { display: "none" }, "&.Mui-expanded": { margin: "8px 0" } } } },
      MuiAccordionSummary: { styleOverrides: { root: { minHeight: 48 }, content: { margin: "12px 0" } } },
      MuiListItemButton: { styleOverrides: { root: { borderRadius: 8, "&.Mui-selected": { backgroundColor: alpha(main, 0.12), color: main } } } },
    },
  };
};

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
  setModeString: () => {},
  currentMode: "dark",
});

export const useMode = () => {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "dark";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prev) => (prev === "dark" ? "light" : "dark")),
      setModeString: (newMode) => setMode(newMode === "light" ? "light" : "dark"),
      currentMode: mode,
    }),
    [mode]
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};
